/**
 * VistoeraTASK-00575 / VistoeraTASK-00593 / VistoeraTASK-00685 —
 * Workspace Image Assets hi-res viewer.
 *
 * Directly reuses CanvasHiResImageViewer (same Fit / wheel zoom / pan /
 * progressive master load). Bottom chrome: asset info + Edit / Download / Close.
 * Edit navigates via the caller's existing Canvas image-editor entry — no second editor.
 *
 * Optional folder-scoped prev/next (TASK-00685) is additive: callers without
 * navigation context keep single-image behavior.
 */

import { DownloadOutlined, EditOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Button, Space } from 'antd'
import { useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import CanvasHiResImageViewer from './contentProduction/canvas/CanvasHiResImageViewer'
import './contentProduction/canvas/production-canvas.css'

export interface ImageAssetHiResViewerProps {
  open: boolean
  onClose: () => void
  /** Immediate display (web/proxy). */
  displaySrc: string | null
  /** Progressive upgrade target (original / full). */
  masterSrc?: string | null
  /** Detail fetch still in flight — reserved for callers; display uses src when ready. */
  loading?: boolean
  filename: string
  /** Compact asset metadata grid (below the viewport). */
  meta: ReactNode
  canDownload?: boolean
  onDownload?: () => void
  /** Show Edit → existing Canvas image editor (VistoeraTASK-00593). */
  canEdit?: boolean
  onEdit?: () => void
  isTransparent?: boolean
  /** VistoeraTASK-00685 — folder-scoped prev/next (omit = single-image mode). */
  hasPrev?: boolean
  hasNext?: boolean
  onPrev?: () => void
  onNext?: () => void
  navBusy?: boolean
}

function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return Boolean(target.closest('[contenteditable="true"], input, textarea, select'))
}

export default function ImageAssetHiResViewer({
  open,
  onClose,
  displaySrc,
  masterSrc = null,
  loading: _loading = false,
  filename: _filename,
  meta,
  canDownload = false,
  onDownload,
  canEdit = false,
  onEdit,
  isTransparent = false,
  hasPrev = false,
  hasNext = false,
  onPrev,
  onNext,
  navBusy = false,
}: ImageAssetHiResViewerProps) {
  const { t } = useTranslation('imageAssets')
  const { t: tActions } = useTranslation('actions')

  // ArrowLeft / ArrowRight only while this viewer is open; skip editable
  // fields and never wrap. ESC close stays in CanvasHiResImageViewer.
  // Rapid repeats are fine — the page supersedes in-flight loads by seq.
  useEffect(() => {
    if (!open) return
    if (!hasPrev && !hasNext) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      if (isEditableKeyboardTarget(e.target)) return
      if (e.key === 'ArrowLeft') {
        if (!hasPrev || !onPrev) return
        e.preventDefault()
        e.stopPropagation()
        onPrev()
        return
      }
      if (!hasNext || !onNext) return
      e.preventDefault()
      e.stopPropagation()
      onNext()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, hasPrev, hasNext, onPrev, onNext])

  const navOverlay =
    hasPrev || hasNext ? (
      <>
        {hasPrev ? (
          <button
            type="button"
            className={`iac-hires-nav is-prev${navBusy ? ' is-busy' : ''}`}
            aria-label={t('preview.nav.prev')}
            data-testid="iac-hires-nav-prev"
            onClick={() => onPrev?.()}
          >
            <LeftOutlined />
          </button>
        ) : null}
        {hasNext ? (
          <button
            type="button"
            className={`iac-hires-nav is-next${navBusy ? ' is-busy' : ''}`}
            aria-label={t('preview.nav.next')}
            data-testid="iac-hires-nav-next"
            onClick={() => onNext?.()}
          >
            <RightOutlined />
          </button>
        ) : null}
      </>
    ) : null

  return (
    <CanvasHiResImageViewer
      open={open && Boolean(displaySrc || masterSrc)}
      onClose={onClose}
      displaySrc={displaySrc}
      masterSrc={masterSrc}
      hideDefaultClose
      hideMetaChip
      showZoomToolbar
      viewportOverlay={navOverlay}
      className="is-workbench-portal"
      viewportClassName={isTransparent ? 'is-transparent' : undefined}
      bottomPanel={
        <div className="iac-hires-viewer-footer" data-testid="iac-hires-viewer-footer">
          <div className="iac-hires-viewer-info">
            <dl className="iac-preview-meta">{meta}</dl>
          </div>
          <div className="iac-hires-viewer-actions">
            <Space>
              {canEdit ? (
                <Button
                  icon={<EditOutlined />}
                  onClick={onEdit}
                  data-testid="iac-hires-edit"
                >
                  {tActions('edit')}
                </Button>
              ) : null}
              {canDownload ? (
                <Button icon={<DownloadOutlined />} onClick={onDownload}>
                  {t('preview.downloadOriginal')}
                </Button>
              ) : null}
              <Button onClick={onClose}>{tActions('close')}</Button>
            </Space>
          </div>
        </div>
      }
    />
  )
}
